import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  IconButton,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField
} from '@mui/material';
import { Close, ExpandMore } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useCalculator } from '../../../../contexts/CalculatorContext';
import { usePricing } from '../../../../contexts/PricingContext';
import { useUserPreferences } from '../../../../contexts/UserPreferencesContext';
import {
  convertDisplayToUSD,
  convertUSDToDisplay,
  getCurrencySymbolByCode,
  getEffectiveDisplayCurrency,
} from '../../../../utils/currencyUtils';

const roundDisplay = (value) => {
  if (value == null || isNaN(value)) return '0';
  return String(Math.round(Number(value)));
};

const VoiceBotSettingsDialog = () => {
  const { t } = useTranslation();
  const {
    voicebotAiMode,
    voicebotSettingsOpen,
    setVoicebotAiMode,
    voicebotImplementationFee,
    enableVoicebotImplementationFee,
    voicebotExtraChannels,
    veryCustomProductDiscounts,
    handleUpdateVeryCustomProductDiscount,
    handleApplyVoicebotSettings
  } = useCalculator();
  const { currencyRates } = usePricing();
  const { region, displayCurrency } = useUserPreferences();
  const effectiveCurrency = getEffectiveDisplayCurrency(region, displayCurrency);
  const effectiveSymbol = getCurrencySymbolByCode(effectiveCurrency);

  // Implementation Fee state — stored as USD in context, edited in display currency.
  const [localImplementationFee, setLocalImplementationFee] = React.useState(voicebotImplementationFee); // USD
  const [localEnableImplementationFee, setLocalEnableImplementationFee] = React.useState(enableVoicebotImplementationFee);
  const [implementationFeeString, setImplementationFeeString] = React.useState(
    () => roundDisplay(convertUSDToDisplay(voicebotImplementationFee, effectiveCurrency, currencyRates))
  );

  // Extra Channels state
  const [localExtraChannels, setLocalExtraChannels] = React.useState(voicebotExtraChannels);
  const [extraChannelsString, setExtraChannelsString] = React.useState(String(voicebotExtraChannels));

  // Advanced Settings state
  const currentDiscount = veryCustomProductDiscounts['voicebot'] || 0;
  const [isAdvancedMode, setIsAdvancedMode] = React.useState(currentDiscount > 0);
  const [localDiscount, setLocalDiscount] = React.useState(currentDiscount);
  const [discountString, setDiscountString] = React.useState(String(currentDiscount));

  // Reset local state when dialog opens
  React.useEffect(() => {
    if (voicebotSettingsOpen) {
      const currentDiscount = veryCustomProductDiscounts['voicebot'] || 0;
      setLocalDiscount(currentDiscount);
      setDiscountString(String(currentDiscount));
      setIsAdvancedMode(currentDiscount > 0);

      // Reset implementation fee state — convert stored USD back into display currency for the input.
      setLocalImplementationFee(voicebotImplementationFee);
      setLocalEnableImplementationFee(enableVoicebotImplementationFee);
      setImplementationFeeString(roundDisplay(convertUSDToDisplay(voicebotImplementationFee, effectiveCurrency, currencyRates)));

      // Reset extra channels state
      setLocalExtraChannels(voicebotExtraChannels);
      setExtraChannelsString(String(voicebotExtraChannels));
    }
  }, [voicebotSettingsOpen, veryCustomProductDiscounts, voicebotImplementationFee, enableVoicebotImplementationFee, voicebotExtraChannels, effectiveCurrency, currencyRates]);

  // Handle keyboard shortcuts for advanced mode
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'C') {
        event.preventDefault();
        setIsAdvancedMode(prev => !prev);
      }
    };

    if (voicebotSettingsOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [voicebotSettingsOpen]);

  // Handle double click on title for advanced mode
  const handleTitleDoubleClick = () => {
    setIsAdvancedMode(prev => !prev);
  };

  // Handle implementation fee input changes
  const handleImplementationFeeChange = (e) => {
    setImplementationFeeString(e.target.value);
  };

  const handleImplementationFeeBlur = () => {
    const numValue = Number(implementationFeeString);
    if (isNaN(numValue) || numValue < 0) {
      setLocalImplementationFee(0);
      setImplementationFeeString('0');
      return;
    }
    // User typed in display currency → store USD internally for consistent aggregation.
    const usdValue = convertDisplayToUSD(numValue, effectiveCurrency, currencyRates);
    setLocalImplementationFee(usdValue);
    setImplementationFeeString(roundDisplay(numValue));
  };

  const handleEnableImplementationFeeChange = (event) => {
    setLocalEnableImplementationFee(event.target.checked);
  };

  // Handle extra channels input changes
  const handleExtraChannelsChange = (e) => {
    setExtraChannelsString(e.target.value);
  };

  const handleExtraChannelsBlur = () => {
    const numValue = Number(extraChannelsString);
    if (isNaN(numValue) || numValue < 0) {
      setLocalExtraChannels(0);
      setExtraChannelsString('0');
    } else {
      const intValue = Math.floor(numValue);
      setLocalExtraChannels(intValue);
      setExtraChannelsString(String(intValue));
    }
  };

  // Handle discount input changes
  const handleDiscountChange = (e) => {
    setDiscountString(e.target.value);
  };

  const handleDiscountBlur = () => {
    const numValue = Number(discountString);
    if (isNaN(numValue) || numValue < 0) {
      setLocalDiscount(0);
      setDiscountString('0');
    } else if (numValue > 100) {
      setLocalDiscount(100);
      setDiscountString('100');
    } else {
      setLocalDiscount(numValue);
      setDiscountString(String(numValue));
    }
  };

  const handleClose = () => {
    handleApplyVoicebotSettings(); // This will close the dialog
  };

  const handleApply = () => {
    // Apply very custom product discount
    handleUpdateVeryCustomProductDiscount('voicebot', localDiscount);

    // Check if there are any actual custom settings
    const hasAnyRealCustomSettings = !voicebotAiMode || (localDiscount > 0) || (localEnableImplementationFee && localImplementationFee > 0) || localExtraChannels > 0;

    // Apply all settings through the handler (which will auto-save)
    handleApplyVoicebotSettings({
      voicebotAiMode,
      hasVoicebotCustomSettings: hasAnyRealCustomSettings,
      voicebotImplementationFee: localImplementationFee,
      enableVoicebotImplementationFee: localEnableImplementationFee,
      voicebotExtraChannels: localExtraChannels
    });
  };

  const handleAiModeChange = (event) => {
    setVoicebotAiMode(event.target.checked);
  };

  const totalChannels = 3 + localExtraChannels;
  const extraChannelsCost = localExtraChannels * 25;

  return (
    <Dialog
      open={voicebotSettingsOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogTitle onDoubleClick={handleTitleDoubleClick} sx={{ cursor: 'pointer', userSelect: 'none' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('calculator.voicebotSettings', 'VoiceBot Settings')}
          </Typography>

          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ py: 3 }}>
        {/* Mode Switch */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            {t('calculator.voicebotMode', 'Campaign Mode')}
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={voicebotAiMode}
                onChange={handleAiModeChange}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {t('calculator.voicebotAiMode', 'AI VoiceBot')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('calculator.voicebotAiDescription', 'AI-powered voice conversations (1000 units per minute)')}
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', ml: 0 }}
          />
        </Box>

        {!voicebotAiMode && (
          <Box sx={{
            p: 2,
            bgcolor: 'info.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'info.200'
          }}>
            <Typography variant="body2" color="info.main" sx={{ fontWeight: 500 }}>
              {t('calculator.voicebotRoboCallMode', 'RoboCall')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('calculator.voicebotRoboCallDescription', 'Automated calls without AI (100 units per minute)')}
            </Typography>
          </Box>
        )}

        {/* Extra Robo-Channels Section */}
        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            {t('calculator.voicebotExtraChannels', 'Extra Robo-Channels')}
          </Typography>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('calculator.voicebotChannelsIncluded', '3 channels included in every plan')}
          </Typography>

          <Box sx={{ mt: 1.5 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('calculator.voicebotExtraChannelsCount', 'Additional channels')} ({t('calculator.voicebotChannelPrice', '$25/month per channel')})
            </Typography>

            <TextField
              size="small"
              type="number"
              value={extraChannelsString}
              onChange={handleExtraChannelsChange}
              onBlur={handleExtraChannelsBlur}
              InputProps={{
                inputProps: { min: 0, step: 1 }
              }}
              sx={{ width: 120 }}
              helperText={localExtraChannels > 0
                ? `${totalChannels} ${t('calculator.voicebotTotalChannels', 'total channels')} • +$${extraChannelsCost}/${t('calculator.voicebotPerMonth', 'mo')}`
                : `3 ${t('calculator.voicebotTotalChannels', 'total channels')}`
              }
            />
          </Box>
        </Box>

        {/* Implementation Fee Section */}
        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            {t('calculator.voicebotImplementationFee', 'Implementation Fee')}
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={localEnableImplementationFee}
                onChange={handleEnableImplementationFeeChange}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {t('calculator.enableImplementationFee', 'Enable implementation fee')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('calculator.voicebotImplementationFeeDesc', 'One-time fee for VoiceBot implementation and setup')}
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', ml: 0 }}
          />

          {localEnableImplementationFee && (
            <Box sx={{ mt: 2, ml: 0 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('calculator.implementationFeeAmount', 'Implementation fee amount')} ({effectiveCurrency})
              </Typography>

              <TextField
                size="small"
                type="number"
                value={implementationFeeString}
                onChange={handleImplementationFeeChange}
                onBlur={handleImplementationFeeBlur}
                InputProps={{
                  startAdornment: effectiveSymbol,
                  inputProps: { min: 0, step: 1 }
                }}
                sx={{ width: 150 }}
                helperText={Number(implementationFeeString) > 0 ? `${effectiveSymbol}${implementationFeeString} one-time fee` : 'No fee'}
              />
            </Box>
          )}
        </Box>

        {/* Advanced Settings */}
        {isAdvancedMode && (
          <>
            <Divider sx={{ my: 3 }} />

            <Accordion
              defaultExpanded
              elevation={0}
              sx={{
                border: '1px solid rgba(0, 0, 0, 0.12)',
                borderRadius: 1,
                '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  bgcolor: 'rgba(255, 152, 0, 0.08)',
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center'
                  }
                }}
              >
                <Typography variant="subtitle2" sx={{ color: '#FF9800', fontWeight: 600 }}>
                  {t('calculator.advancedSettings', 'Advanced Settings')}
                </Typography>
              </AccordionSummary>

              <AccordionDetails sx={{ pt: 2 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('calculator.veryCustomProductDiscount', 'Custom Product Discount')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {t('calculator.customDiscountNote', 'Applied after bundle discounts (0-100%)')}
                  </Typography>

                  <TextField
                    size="small"
                    type="number"
                    value={discountString}
                    onChange={handleDiscountChange}
                    onBlur={handleDiscountBlur}
                    InputProps={{
                      endAdornment: '%',
                      inputProps: { min: 0, max: 100, step: 1 }
                    }}
                    sx={{ width: 120 }}
                    helperText={localDiscount > 0 ? `${localDiscount}% discount applied` : 'No discount'}
                  />
                </Box>

                <Typography variant="caption" color="text.secondary">
                  {t('calculator.advancedSettingsHint', 'Access: Double-click title or Ctrl+Shift+C')}
                </Typography>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} color="inherit">
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          sx={{ ml: 1 }}
        >
          {t('common.apply', 'Apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VoiceBotSettingsDialog;
